import React, { useState, useEffect } from "react";

//CSS
import "./unitPopups.css";
import "../../Pages/0PageStyles.css";

//Components
import Button from "../Other Components/button";
import InputField from "../Other Components/inputField";
import PopupConfirmation from "../Other Components/popupConfirmation";
import PageLoader from "../Other Components/pageLoader";
import MandatoryExplanation from "../Other Components/mandatoryExplanation";

//Functions and hooks
import {
  GetUnitData,
  EditUnit,
  DeleteUnit,
} from "../../BackendAccess/UnitHooks";
import { useNotification } from "../../Contexts/NotificationContext";
import { useErrorHandler } from "../../hooks/errorHandler";
import useFormState from "../../hooks/useFormStates";
import { useTranslation } from "react-i18next";
import { usePopup } from "../../Contexts/PopupContext";

export default function UnitEditPopup({
  currentID,
  onCancel,
  onEdit,
  onDelete,
}) {
  const { formData, setField, loadInitialData, hasChanged } = useFormState({});

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState(null);

  const [editing, setEditing] = useState(false);
  const [deleteBtnDisabled, setDeleteBtnDisabled] = useState(false);

  const { showNotification } = useNotification();
  useErrorHandler(error, showNotification, setPageError);
  const { t } = useTranslation();

  const { showPopup, removePopup } = usePopup();

  const showConfirmationPopup = () => {
    const key = Date.now();
    showPopup(
      <PopupConfirmation
        style={"warning"}
        message={
          <p>
            {t("Are you sure you want to delete this unit?", { ns: "units" })}
            <br />
            <span className="text-negative">
              {t("This action cannot be undone.")}
            </span>
          </p>
        }
        onConfirm={() => handleConfirmDelete(key)}
        onCancel={() => handleCancelDelete(key)}
      />,
      key
    );
  };

  ////////Load data ///////////////////////////////////////
  const GetAndLoadData = async () => {
    setLoading(true);
    try {
      const [data, error] = await GetUnitData(currentID);
      if (error) {
        error.isPageError = true;
        setError(error);
      } else LoadData(data);
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentID) {
      GetAndLoadData();
    }
  }, [currentID]);

  async function LoadData(unit) {
    loadInitialData({
      ...unit,
      LastEditedBy:
        unit.FirstName + " " + unit.LastName + " (" + unit.LastEditedBy + ")",
    });
  }
  ////////Load data END///////////////////////////////////////

  ////////Handle imput changes///////////////////////////////////////
  const handleChange = (field, value) => {
    setField(field, value);
  };
  ////////Handle imput changes END///////////////////////////////////////

  const OnConfirmChanges = async () => {
    showNotification(t("Editing unit...", { ns: "units" }), "info");
    setEditing(true);
    try {
      const [data, error] = await EditUnit(
        currentID,
        formData.UnitID,
        formData.Name
      );

      if (error) {
        setError(error);
        return;
      }
      showNotification(
        t("Unit edited successfully!", { ns: "units" }),
        "success"
      );
      await onEdit();
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setEditing(false);
    }
  };

  const clickOnDelete = () => {
    showConfirmationPopup();
  };

  const handleConfirmDelete = async (key) => {
    showNotification(t("Deleting unit...", { ns: "units" }), "info");
    setDeleteBtnDisabled(true);
    try {
      const [data, error] = await DeleteUnit(currentID);
      if (error) {
        setError(error);
        return;
      }
      showNotification(
        t("Unit deleted successfully!", { ns: "units" }),
        "success"
      );
      onDelete();
    } catch (err) {
      setError({
        err: t("server.Server error.", { ns: "error" }),
        errCode: 500,
      });
    } finally {
      setDeleteBtnDisabled(false);
      removePopup(key);
    }
  };

  const handleCancelDelete = (key) => {
    removePopup(key);
  };

  const clickOnCancel = () => {
    showNotification(t("Unit editing canceled.", { ns: "units" }), "info");
    onCancel();
  };
  return (
    <div className="input-popup">
      <div className="dimming-cover" />
      <div className="input-popup-container">
        <PageLoader loading={loading} error={pageError}>
          <div className="input-popup-fields-container units">
            <InputField
              placeholder={t("Example: 1001")}
              label={t("ID") + ":"}
              value={formData.UnitID}
              onChange={(e) => {
                handleChange("UnitID", e);
              }}
              mandatory={true}
              readOnly={true}
            />

            <InputField
              placeholder={t("Example: kg", { ns: "units" })}
              label={t("Name") + ":"}
              value={formData.Name}
              onChange={(e) => {
                handleChange("Name", e);
              }}
              mandatory={true}
            />
          </div>
          <MandatoryExplanation />

          <div className="btn-container">
            <Button
              btnMargin={"btn-default"}
              btnStyle={"btn-positive"}
              onClick={OnConfirmChanges}
              disabled={!hasChanged || editing}
              acceptEnter={true}
              icon={"apply"}
            >
              {t("Apply Changes")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-primary"
              onClick={clickOnCancel}
            >
              {t("Cancel")}
            </Button>
            <Button
              btnMargin={"btn-default"}
              btnStyle="btn-negative"
              onClick={clickOnDelete}
              icon={"delete"}
              disabled={deleteBtnDisabled}
            >
              {t("Delete")}
            </Button>
          </div>
          <span className="last-edited-by-bar w-100% mt-0 mb-0">
            <span className="sec-textcolor-span ">{t("Last edited by:")} </span>
            &nbsp;
            {formData.LastEditedBy}
          </span>
        </PageLoader>
      </div>
    </div>
  );
}
